import React, { useEffect, useMemo, useState } from 'react';
import {
  Badge,
  Box,
  Button,
  Card,
  CardBody,
  Code,
  FormControl,
  FormLabel,
  Heading,
  HStack,
  IconButton,
  Spinner,
  Stack,
  Text,
  Select,
  Tooltip,
  useToast,
} from '@chakra-ui/react';
import { CopyIcon, ExternalLinkIcon } from '@chakra-ui/icons';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { useUser } from '../../contexts/UserContext';
import { getLead, updateLeadStatus } from '../../api/leads';
import { canEditLeadStatus } from './leadPermissions';
import { getUserRoles, isConsultantUser } from '../../utils/authRoles';

const DEBUG = true;

const STATUS_OPTIONS = [
  { label: 'New', value: 'new' },
  { label: 'In progress', value: 'in_progress' },
  { label: 'Booked', value: 'booked' },
  { label: 'Converted', value: 'converted' },
  { label: 'Archived', value: 'archived' },
];

export const formatDate = (value) => {
  if (!value) return '—';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '—';
  return date.toLocaleString();
};

export const statusLabel = (status) =>
  status
    ?.toString()
    .replaceAll('_', ' ')
    .replace(/\b\w/g, (char) => char.toUpperCase()) || 'Unknown';

export const statusColor = (status) => {
  switch (status) {
    case 'new':
      return 'blue';
    case 'in_progress':
      return 'orange';
    case 'booked':
      return 'purple';
    case 'converted':
      return 'green';
    case 'archived':
      return 'gray';
    default:
      return 'gray';
  }
};

export const assignedAdvisorName = (lead) =>
  lead?.consultant_advisor_name ||
  lead?.consultant_advisor?.name ||
  lead?.assigned_consultant_advisor?.name ||
  lead?.advisor_name ||
  lead?.advisor?.name ||
  lead?.assigned_advisor?.name ||
  null;

export const assignedConsultantName = (lead) =>
  lead?.consultant_name ||
  lead?.consultant?.name ||
  lead?.assigned_consultant?.name ||
  null;

export const assignedText = (lead) => {
  const consultant = assignedConsultantName(lead);
  const advisor = assignedAdvisorName(lead);
  const hasAdvisorAssignment =
    lead?.consultant_advisor_id != null ||
    lead?.consultant_advisor?.id != null ||
    lead?.assigned_consultant_advisor_id != null ||
    lead?.assigned_consultant_advisor?.id != null ||
    Boolean(lead?.consultant_advisor_name);

  if (hasAdvisorAssignment) {
    return `${consultant || 'Unknown consultant'}:${advisor || 'Unknown advisor'}`;
  }

  return consultant || 'Unassigned';
};

export const toolText = (lead) => lead?.tool || lead?.tool_key || lead?.source || lead?.metadata?.tool || '—';
export const bookingUrl = (lead) => lead?.booking_url || lead?.advisor?.booking_url || lead?.consultant?.booking_url || null;

export const leadMetadata = (lead) => {
  const explicitMetadata = lead?.metadata;
  if (explicitMetadata && typeof explicitMetadata === 'object' && !Array.isArray(explicitMetadata)) {
    return explicitMetadata;
  }

  const fallback = {
    tool_key: lead?.tool_key ?? lead?.tool ?? null,
    attempted_consultant_slug: lead?.consultant_slug ?? lead?.attempted_consultant_slug ?? null,
    attempted_advisor_slug: lead?.advisor_slug ?? lead?.attempted_advisor_slug ?? null,
    referral_source: lead?.referral_source ?? lead?.source ?? null,
    created_at: lead?.created_at ?? null,
  };

  return Object.fromEntries(
    Object.entries(fallback).filter(([, value]) => value !== null && value !== undefined && value !== '')
  );
};

export const LeadStatusControl = ({ lead, user, isUpdating, onChange }) => {
  const roles = getUserRoles(user) || [];
  const canEdit = canEditLeadStatus(user, lead);
  const isConsultantAdvisor = roles.includes('consultant_advisor');
  const isReadOnly = !canEdit;

  if (DEBUG) {
    console.log('[LeadStatusControl] debug context', {
      roles,
      legacyRole: user?.role,
      permissions: user?.permissions,
      userEmail: user?.email,
      leadAdvisorEmail: lead?.consultant_advisor?.email,
      leadAssignees: {
        consultant_id: lead?.consultant_id,
        consultant: lead?.consultant,
        consultant_advisor_id: lead?.consultant_advisor_id,
        consultant_advisor: lead?.consultant_advisor,
      },
    });
    console.log('[LeadStatusControl] canEditLeadStatus', canEdit);
  }

  if (isReadOnly) {
    return (
      <Stack spacing={1} align="flex-start">
        <Badge colorScheme={statusColor(lead?.status)}>{statusLabel(lead?.status)}</Badge>
        {isConsultantAdvisor ? (
          <>
            <Text fontSize="xs" color="gray.500">Read-only. Only assigned advisor can update.</Text>
            <Text fontSize="xs" color="gray.500">
              Assigned advisor: {assignedAdvisorName(lead) || 'Unassigned'}
            </Text>
          </>
        ) : null}
      </Stack>
    );
  }

  return (
    <Select
      size="sm"
      maxW="170px"
      value={lead?.status || 'new'}
      onChange={(event) => onChange(event.target.value)}
      isDisabled={isUpdating}
    >
      {STATUS_OPTIONS.map((status) => (
        <option key={status.value} value={status.value}>{status.label}</option>
      ))}
    </Select>
  );
};

export const LeadDetailsContent = ({ lead, user, isUpdating = false, onStatusChange }) => {
  const toast = useToast();
  const metadata = useMemo(() => leadMetadata(lead), [lead]);

  const copyEmail = async () => {
    if (!lead?.email) return;
    try {
      await navigator.clipboard.writeText(lead.email);
      toast({ title: 'Email copied', status: 'success', duration: 2000, isClosable: true });
    } catch (error) {
      toast({ title: 'Could not copy email', status: 'error', duration: 2500, isClosable: true });
    }
  };

  if (!lead) return null;

  return (
    <Stack spacing={5}>
      <Box>
        <Text fontSize="sm" color="gray.500">Name</Text>
        <Text fontWeight="semibold">{lead.name || '—'}</Text>
      </Box>

      <Box>
        <Text fontSize="sm" color="gray.500">Status</Text>
        <LeadStatusControl
          lead={lead}
          user={user}
          isUpdating={isUpdating}
          onChange={onStatusChange}
        />
      </Box>

      <Box>
        <Text fontSize="sm" color="gray.500">Contact</Text>
        <Text>Email: {lead.email || '—'}</Text>
        <Text>Phone: {lead.phone || '—'}</Text>
        <Text>Organization: {lead.organization_name || '—'}</Text>
      </Box>

      <Box>
        <Text fontSize="sm" color="gray.500">Routing</Text>
        <Text>Assigned: {assignedText(lead)}</Text>
        <Text>Tool: {toolText(lead)}</Text>
        <Text>Created: {formatDate(lead.created_at)}</Text>
      </Box>

      <Box>
        <Text fontSize="sm" color="gray.500" mb={2}>Metadata</Text>
        {Object.keys(metadata).length === 0 ? (
          <Text color="gray.500">No metadata available for this lead.</Text>
        ) : (
          <Code display="block" whiteSpace="pre-wrap" p={3} borderRadius="md">
            {JSON.stringify(metadata, null, 2)}
          </Code>
        )}
      </Box>

      <HStack>
        <Tooltip label="Copy email" hasArrow>
          <IconButton
            icon={<CopyIcon />}
            aria-label="Copy email"
            size="sm"
            variant="outline"
            onClick={copyEmail}
            isDisabled={!lead.email}
          />
        </Tooltip>
        {bookingUrl(lead) && (
          <Button
            as="a"
            href={bookingUrl(lead)}
            target="_blank"
            rel="noopener noreferrer"
            rightIcon={<ExternalLinkIcon />}
            variant="outline"
            size="sm"
          >
            Open booking link
          </Button>
        )}
      </HStack>
    </Stack>
  );
};

const LeadDetails = () => {
  const { id } = useParams();
  const { user } = useUser();
  const navigate = useNavigate();
  const toast = useToast();
  const leadsPath = isConsultantUser(user) ? '/app/consultant/leads' : '/app/admin/leads';

  const [lead, setLead] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isUpdating, setIsUpdating] = useState(false);

  useEffect(() => {
    const loadLead = async () => {
      setIsLoading(true);
      try {
        const payload = await getLead(id);
        setLead(payload);
      } catch (error) {
        console.error('Error loading lead:', error);
        toast({ title: 'Could not load lead', status: 'error', duration: 4000, isClosable: true });
        setLead(null);
      } finally {
        setIsLoading(false);
      }
    };

    loadLead();
  }, [id, toast]);

  const handleStatusChange = async (status) => {
    if (!lead) return;
    const previousLead = lead;

    setLead((prev) => ({ ...prev, status }));
    setIsUpdating(true);
    try {
      const updated = await updateLeadStatus(lead.id, status);
      setLead((prev) => ({ ...prev, ...updated }));
      toast({ title: 'Lead status updated', status: 'success', duration: 2500, isClosable: true });
    } catch (error) {
      setLead(previousLead);
      const responseStatus = error?.response?.status;
      toast({
        title: responseStatus === 403 || responseStatus === 404 ? 'Not authorized to update this lead' : 'Could not update status',
        status: 'error',
        duration: 3500,
        isClosable: true,
      });
    } finally {
      setIsUpdating(false);
    }
  };

  if (isLoading) {
    return (
      <Box p={6}>
        <Spinner />
      </Box>
    );
  }

  if (!lead) {
    return (
      <Box p={6}>
        <Heading size="md" mb={3}>Lead not found</Heading>
        <Button onClick={() => navigate(-1)} variant="outline">
          Back
        </Button>
      </Box>
    );
  }

  return (
    <Box p={6}>
      <HStack justify="space-between" mb={6} wrap="wrap">
        <Heading as="h2" size="lg">Lead Details</Heading>
        <Button as={Link} to={leadsPath} variant="outline">
          Back to Leads
        </Button>
      </HStack>

      <Card borderWidth="1px" borderRadius="lg" bg="white">
        <CardBody>
          <LeadDetailsContent
            lead={lead}
            user={user}
            isUpdating={isUpdating}
            onStatusChange={handleStatusChange}
          />
        </CardBody>
      </Card>
    </Box>
  );
};

export default LeadDetails;
